// --- START OF FILE SecurityFramework.js ---

import React, { useReducer, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import { useSafeSave } from '../../../hooks/useSafeSave';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useBroadcast } from '../../../hooks/useBroadcast';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import frameworkDefinitionService from '../../../services/frameworkDefinitionService';
import securityControlsService from '../../../services/securityControlsService';
import api from '../../../api/apiClient'; // Import API client for AI matching
import { handleAIError } from '../../../utils/aiErrorHandler';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showInfoToast } from '../../../utils/toastUtils';
import { Shield, Info, FileText, Upload, Save, BrainCircuit } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../common/Button';
import WorkshopExportMenu from '../../common/WorkshopExportMenu';
import GuideModal from './GuideModal';
import SecurityFrameworkPdfGenerator from '../../../utils/securityFrameworkPdfGenerator';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal';

// Import components (assuming FrameworkComponents.js is already updated)
import {
    FrameworkListItem, RuleItem, SelectedRuleCard,
    SecurityFrameworkSummaryTable
} from './FrameworkComponents';
import { Card, Input } from './UIComponents';

// ** Step 2: Use the real framework definition service **
import securityFrameworkReducer, { initialState, ACTION_TYPES } from './SecurityFrameworkReducer';
import { parseCSV, getFrameworkColor } from './SecurityFrameworkUtils';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { normalizeRuleStatus } from '../../../utils/ruleStatusNormalizer';


// --- Helper Function ---
/*
const ensureShortId = (item, prefix = '') => {
    if (item.shortId) return item.shortId;
    const idStr = String(item.id);
    const randomPart = Math.random().toString(36).substring(2, 5);
    return `${prefix}${idStr.substring(idStr.length - 4, idStr.length)}-${randomPart}`.toUpperCase();
};
*/

// Helper functions parseCSV and getFrameworkColor moved to SecurityFrameworkUtils.js

// Reducer logic moved to SecurityFrameworkReducer.js

// --- Selected Rules Pagination Component ---
const SelectedRulesWithPagination = ({
    selectedRulesDetails,
    ruleCardRefs,
    handleToggleRule,
    handleRuleChange,
    handleUpdateRuleBusinessValues,
    getActualFrameworkKey,
    selectedFramework,
    currentBusinessValues,
    currentDreadedEvents,
    state,
    t,
    ruleIdToScrollTo,
    onScrollComplete
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(3); // Default to 3 rules per page for better space management

    // Calculate pagination
    const totalItems = selectedRulesDetails.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentRules = selectedRulesDetails.slice(startIndex, endIndex);

    // Get pages to display (with ellipsis)
    const getVisiblePages = (current, total) => {
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
        if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    // Reset to first page when rules change
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [totalItems, totalPages, currentPage]);

    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Effect to handle scrolling and pagination syncing
    useEffect(() => {
        if (ruleIdToScrollTo) {
            // Find which page this rule is on
            const ruleIndex = selectedRulesDetails.findIndex(r => r.id === ruleIdToScrollTo);
            if (ruleIndex !== -1) {
                const requiredPage = Math.floor(ruleIndex / itemsPerPage) + 1;
                if (requiredPage !== currentPage) {
                    setCurrentPage(requiredPage);
                    // Return early so the component can re-render with the new page.
                    // The effect will run again after the render.
                    return;
                }
            }

            // We are on the correct page. Wait briefly for refs to be attached if we just rendered.
            const timer = setTimeout(() => {
                const cardElement = ruleCardRefs.current[ruleIdToScrollTo];
                if (cardElement) {
                    cardElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    cardElement.classList.add('ring-2', 'ring-offset-2', 'ring-yellow-400', 'transition-all', 'duration-1000', 'ease-out');
                    setTimeout(() => {
                        cardElement.classList.remove('ring-2', 'ring-offset-2', 'ring-yellow-400', 'transition-all', 'duration-1000', 'ease-out');
                    }, 1500);
                }
                
                if (onScrollComplete) {
                    onScrollComplete();
                }
            }, 50);
            
            return () => clearTimeout(timer);
        }
    }, [ruleIdToScrollTo, selectedRulesDetails, itemsPerPage, currentPage, ruleCardRefs, onScrollComplete]);

    return (
        <div className="space-y-4">
            {/* Pagination Controls - Top */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center flex-wrap gap-4">
                    <span className="text-sm text-gray-600">
                        {t('securityFramework.pagination.showing')} {startIndex + 1}-{Math.min(endIndex, totalItems)} {t('securityFramework.pagination.of')} {totalItems}
                    </span>
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">{t('securityFramework.pagination.itemsPerPage')}</label>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value={3}>3</option>
                            <option value={5}>5</option>
                            <option value={8}>8</option>
                            <option value={10}>10</option>
                            <option value={selectedRulesDetails.length}>{t('securityFramework.pagination.all')}</option>
                        </select>
                    </div>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center flex-wrap gap-2 mt-4 sm:mt-0 justify-center">
                        <button
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {t('securityFramework.pagination.previous')}
                        </button>

                        <div className="flex items-center flex-wrap gap-1 justify-center">
                            {getVisiblePages(currentPage, totalPages).map((page, index) => (
                                <button
                                    key={`${page}-${index}`}
                                    onClick={() => page !== '...' && goToPage(page)}
                                    disabled={page === '...'}
                                    className={`px-3 py-1 text-sm border rounded ${page === currentPage
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : page === '...'
                                            ? 'border-transparent text-gray-500 cursor-default bg-transparent'
                                            : 'border-gray-300 hover:bg-gray-100'
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {t('securityFramework.pagination.next')}
                        </button>
                    </div>
                )}
            </div>

            {/* Rules List - Fixed Height with Scroll */}
            <div className="max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg bg-white">
                <div className="space-y-6 p-4">
                    {currentRules.map((savedRule) => (
                        <div key={savedRule.id} ref={el => ruleCardRefs.current[savedRule.id] = el}>
                            <SelectedRuleCard
                                rule={savedRule}
                                onRemove={() => handleToggleRule(savedRule.frameworkKey, { id: savedRule.id, name: savedRule.name })}
                                onStatusChange={(e) => handleRuleChange(savedRule.frameworkKey, savedRule.id, 'status', e.target.value)}
                                onJustificationChange={(e) => handleRuleChange(savedRule.frameworkKey, savedRule.id, 'justification', e.target.value)}
                                allBusinessValues={currentBusinessValues || []}
                                onUpdateRuleBusinessValues={handleUpdateRuleBusinessValues}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Pagination Controls - Bottom (only show if more than one page) */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 p-4 bg-gray-50 rounded-lg border">
                    <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t('securityFramework.pagination.previous')}
                    </button>

                    <span className="text-sm text-gray-600">
                        {t('securityFramework.pagination.page')} {currentPage} {t('securityFramework.pagination.of')} {totalPages}
                    </span>

                    <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t('securityFramework.pagination.next')}
                    </button>
                </div>
            )}
        </div>
    );
};

// --- Main Component ---

const SecurityFramework = () => {
    const { t, i18n } = useTranslation();
    const atelier1Store = useAtelier1Store();
    const analysisStore = useAnalysisStore();
    const broadcastUpdate = useBroadcast();
    const { companyAiEnabled } = useAuth();

    const [state, dispatch] = useReducer(securityFrameworkReducer, initialState);
    // console.log("Reducer initialized:", { // This log is fine
    //   hasDispatch: Boolean(dispatch),
    //   hasStateData: Boolean(state),
    //   stateKeys: Object.keys(state)
    // });

    // NEW: State for scrolling to a specific rule card
    const [ruleIdToScrollTo, setRuleIdToScrollTo] = useState(null);
    // NEW: State for showing import info modal
    const [showImportInfo, setShowImportInfo] = useState(false);
    // NEW: Ref to store DOM elements of rule cards
    const ruleCardRefs = useRef({});
    const fileInputRef = useRef(null); // NEW: Ref for hidden file input

    // NEW: State for custom framework name during import
    const [customFrameworkName, setCustomFrameworkName] = useState('');

    // Guide state
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
    const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);

    // NEW: Ref to track if silent language sync has been performed
    const hasPerformedSilentSync = useRef(false);

    // BUGFIX: Guard refs to prevent useEffect re-entrancy loops
    const selectionsLoadedRef = useRef(false);  // Tracks if initial selections were loaded
    const isMigratingRef = useRef(false);       // Prevents migration effect from triggering itself

    // NEW: State for global AI matching
    const [isGlobalAIMatching, setIsGlobalAIMatching] = useState(false);
    const [showAiMatchingModal, setShowAiMatchingModal] = useState(false);
    const [aiMatchingSuggestions, setAiMatchingSuggestions] = useState([]);
    const [showAiRuleSelectionModal, setShowAiRuleSelectionModal] = useState(false);
    const [selectedRulesForAi, setSelectedRulesForAi] = useState([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [ruleToDeleteData, setRuleToDeleteData] = useState(null);
    const [deselectConfirmData, setDeselectConfirmData] = useState(null);
    const [isFrameworkDeleteModalOpen, setIsFrameworkDeleteModalOpen] = useState(false);
    const [frameworkToDelete, setFrameworkToDelete] = useState(null); // {id, name, isNew}

    // NEW: State for global AI matching progress and cancellation
    const [showAiProgressModal, setShowAiProgressModal] = useState(false);
    const abortControllerRef = useRef(null);

    // ** Step 3: Use Context Data + Store Data **
    // A1 data from store, save bridge from context
    const currentAnalysis = analysisStore.currentAnalysis;
    const currentSecurityFramework = atelier1Store.securityFramework;
    const currentBusinessValues = atelier1Store.businessValues;
    const currentDreadedEvents = atelier1Store.dreadedEvents;
    

    // Safe save hook - prevents auto-save from overwriting data when not fully loaded
    const { markDataLoaded, markModified, canAutoSave, clearModified, useFormChangeDetector } = useSafeSave('SecurityFramework');
    const formChangeRef = useFormChangeDetector();

    // BUGFIX: Reset guard refs when analysis changes (handles both mount/remount and stay-mounted cases)
    useEffect(() => {
        selectionsLoadedRef.current = false;
        isMigratingRef.current = false;
    }, [currentAnalysis?.id]);

    // Removed console log for component rendering

    // Derive framework definitions directly from the context data
    // Use _id as stableId to ensure uniqueness per framework (EN/FR have different _id)
    const frameworkDefinitions = useMemo(() => {
        const contextDefinitions = currentSecurityFramework?.definitions || [];
        const stateDefinitions = state.frameworkDefinitions || [];

        const baseDefinitions = stateDefinitions.length > 0 ? stateDefinitions : contextDefinitions;

        return baseDefinitions.map(framework => {
            const stableId = framework.stableId || framework._id || framework.id || framework.type;

            return {
                ...framework,
                stableId: stableId,
                name: framework.name,
                rules: framework.rules?.map(rule => ({
                    ...rule,
                    name: rule.name,
                    description: rule.description
                })) || []
            };
        });
    }, [currentSecurityFramework?.definitions, state.frameworkDefinitions]);

    // Load context definitions into state on initial load or when language changes
    useEffect(() => {
        const contextDefinitions = currentSecurityFramework?.definitions || [];
        const stateDefinitions = state.frameworkDefinitions || [];

        if (contextDefinitions.length > 0) {
            // BUGFIX: keep customs even after is_new stripped (post-save) until backend reflects them
            const contextIds = new Set(contextDefinitions.map(d => d._id || d.id).filter(Boolean));
            const customDefinitions = stateDefinitions.filter(d => {
                const id = d._id || d.id;
                if (!id || contextIds.has(id)) return false;
                // keep unsaved temps and recently-saved customs not yet in context
                return d.type === 'custom' || d.is_new || String(id).startsWith('custom-');
            });

            const mergedDefinitions = [...contextDefinitions, ...customDefinitions];

            dispatch({
                type: 'LOAD_DEFINITIONS_SUCCESS',
                payload: { definitions: mergedDefinitions }
            });
        }
    }, [currentSecurityFramework?.definitions]);


    // Automatically select the framework with the most selected rules
    useEffect(() => {
        if (frameworkDefinitions.length === 0) return;

        const hasSelections = Object.keys(state.selectedRules).length > 0;

        // Wait for selections to load before picking "most rules"
        if (!hasSelections && currentSecurityFramework?.selectedRules &&
            Object.keys(currentSecurityFramework.selectedRules).length > 0) {
            return;
        }

        // If current selection is still valid, keep it
        const currentSelectionValid = state.selectedFrameworkId &&
            frameworkDefinitions.some(f => (f.stableId || f._id || f.id) === state.selectedFrameworkId);
        if (currentSelectionValid) return;

        let frameworkToSelect = null;
        let maxRules = -1;

        for (const framework of frameworkDefinitions) {
            const frameworkKey = framework.stableId || framework._id || framework.id;
            const rules = state.selectedRules[frameworkKey];
            const count = rules ? rules.length : 0;
            if (count > maxRules) {
                maxRules = count;
                frameworkToSelect = frameworkKey;
            }
        }

        if (!frameworkToSelect) {
            frameworkToSelect = frameworkDefinitions[0]?.stableId || frameworkDefinitions[0]?._id || frameworkDefinitions[0]?.id;
            console.log(`[SecurityFramework] Auto-selecting first framework: ${frameworkToSelect}`);
        } else if (maxRules > 0) {
            console.log(`[SecurityFramework] Selected framework with most rules (${maxRules}): ${frameworkToSelect}`);
        }

        if (frameworkToSelect) {
            dispatch({ type: 'SELECT_FRAMEWORK', payload: frameworkToSelect });
        }
    }, [frameworkDefinitions, state.selectedRules, state.selectedFrameworkId]);

    // Update local state if context selections change (e.g., after initial load)
    // BUGFIX: Pre-normalize old _id keys to stableId keys before loading into state.
    // Also guards against re-loading after a save (which updates context but state already has correct data).
    useEffect(() => {
        if (!currentSecurityFramework || frameworkDefinitions.length === 0) return;

        const contextRules = currentSecurityFramework.selectedRules || {};

        // Normalize incoming keys from old _id format to stableId format
        const normalizedRules = {};
        const normalizedCache = { ...(currentSecurityFramework.frameworkNamesCache || {}) };

        Object.entries(contextRules).forEach(([key, rules]) => {
            // Already a stableId key? keep as-is
            const stableMatch = frameworkDefinitions.find(fw => fw.stableId === key);
            if (stableMatch) {
                normalizedRules[key] = rules;
                return;
            }
            // Old _id key? migrate to stableId
            const idMatch = frameworkDefinitions.find(fw => fw._id === key || fw.id === key);
            if (idMatch && idMatch.stableId) {
                normalizedRules[idMatch.stableId] = rules;
                // Also migrate the cache entry
                if (normalizedCache[key] !== undefined) {
                    normalizedCache[idMatch.stableId] = normalizedCache[key];
                    delete normalizedCache[key];
                }
                console.log(`[SecurityFramework] Pre-normalized key on load: ${key} -> ${idMatch.stableId}`);
            } else {
                normalizedRules[key] = rules; // Unknown key, keep — migration effect will handle it
            }
        });

        // If already loaded, only re-load if the normalized incoming data is genuinely different
        // (e.g. data changed from a socket broadcast by another user, not from our own save)
        if (selectionsLoadedRef.current) {
            const currentKeys = Object.keys(state.selectedRules).sort().join(',');
            const incomingKeys = Object.keys(normalizedRules).sort().join(',');
            if (currentKeys === incomingKeys) {
                console.log(`[SecurityFramework] Skipping LOAD_SELECTIONS_SUCCESS — data already loaded and keys match`);
                return;
            }
        }

        console.log(`[SecurityFramework] Loading selections from context (initial load or external change)`);
        console.log(`  - Normalized keys:`, Object.keys(normalizedRules));

        selectionsLoadedRef.current = true;

        dispatch({
            type: 'LOAD_SELECTIONS_SUCCESS',
            payload: {
                savedData: {
                    ...currentSecurityFramework,
                    selectedRules: normalizedRules,
                    frameworkNamesCache: normalizedCache,
                }
            }
        });

        markDataLoaded();
    }, [currentSecurityFramework?.selectedRules, currentSecurityFramework?.ruleBusinessValues, currentSecurityFramework?.ruleDreadedEvents, currentSecurityFramework?.frameworkNamesCache, frameworkDefinitions]);

    // NEW: Populate framework names cache when we have both definitions and selected rules
    useEffect(() => {
        if (frameworkDefinitions.length > 0 && Object.keys(state.selectedRules).length > 0) {
            let needsUpdate = false;
            const updatedCache = { ...state.frameworkNamesCache };

            Object.keys(state.selectedRules).forEach(frameworkKey => {
                if (!updatedCache[frameworkKey]) {
                    let framework = frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === frameworkKey);

                    if (!framework) {
                        const contextFrameworks = currentSecurityFramework?.definitions || [];
                        const originalFramework = contextFrameworks.find(f => (f._id || f.id) === frameworkKey);

                        if (originalFramework && originalFramework.type) {
                            framework = frameworkDefinitions.find(f => f.type === originalFramework.type);
                            console.log(`[SecurityFramework] Cache population: Matched ${frameworkKey} to ${framework?.name} by type ${originalFramework.type}`);
                        }

                        // Third try: Match by name pattern
                        if (!framework && originalFramework) {
                            const cleanName = (originalFramework.name || '').replace(/\s*\([A-Z]{2}\)\s*|\s*V\d+\.\d+\s*|\s*\(.*\)\s*/gi, '').trim().toLowerCase();
                            framework = frameworkDefinitions.find(f => {
                                const fwCleanName = f.name.replace(/\s*\([A-Z]{2}\)\s*|\s*V\d+\.\d+\s*|\s*\(.*\)\s*/gi, '').trim().toLowerCase();
                                return cleanName.includes(fwCleanName) || fwCleanName.includes(cleanName) ||
                                    (cleanName.includes('cis') && fwCleanName.includes('cis')) ||
                                    (cleanName.includes('nist') && fwCleanName.includes('nist')) ||
                                    (cleanName.includes('iso') && fwCleanName.includes('iso')) ||
                                    (cleanName.includes('anssi') && fwCleanName.includes('anssi')) ||
                                    (cleanName.includes('hygiène') && fwCleanName.includes('hygiène'));
                            });
                            if (framework) {
                                console.log(`[SecurityFramework] Cache population: Matched ${frameworkKey} to ${framework.name} by name pattern`);
                            }
                        }
                    }

                    if (framework) {
                        updatedCache[frameworkKey] = framework.name;
                        needsUpdate = true;
                    }
                }
            });

            if (needsUpdate) {
                console.log(`[SecurityFramework] Updating framework names cache:`, updatedCache);
                dispatch({
                    type: 'UPDATE_FRAMEWORK_NAMES_CACHE',
                    payload: { frameworkNamesCache: updatedCache }
                });
            }
        }
    }, [frameworkDefinitions, state.selectedRules, state.frameworkNamesCache, currentSecurityFramework?.definitions]);

    // NEW: Silent language sync - if framework names are still unresolved after initial load, 
    // trigger a quick language toggle to force the migration mechanism
    useEffect(() => {
        if (hasPerformedSilentSync.current) return;
        if (Object.keys(state.selectedRules).length === 0) return;
        if (frameworkDefinitions.length === 0) return;

        const hasUnresolvedNames = Object.keys(state.selectedRules).some(frameworkKey => {
            const cachedName = state.frameworkNamesCache[frameworkKey];
            if (cachedName && !cachedName.startsWith('Framework ')) {
                return false;
            }

            const framework = frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === frameworkKey);
            if (framework && framework.name) {
                return false;
            }

            return true;
        });

        if (hasUnresolvedNames) {
            console.log('[SecurityFramework] Detected unresolved framework names, performing silent language sync...');
            hasPerformedSilentSync.current = true;

            const currentLang = i18n.language;
            const tempLang = currentLang === 'en' ? 'fr' : 'en';

            i18n.changeLanguage(tempLang).then(() => {
                setTimeout(() => {
                    i18n.changeLanguage(currentLang).then(() => {
                        console.log('[SecurityFramework] Silent language sync completed');
                    });
                }, 100);
            });
        } else {
            hasPerformedSilentSync.current = true;
        }
    }, [state.selectedRules, state.frameworkNamesCache, frameworkDefinitions, i18n]);

    // Scrolling effect moved to SelectedRulesWithPagination to handle pagination syncing

    // --- Derived Data --- (Use definitions from state)
    const frameworksMap = useMemo(() => {
        return new Map(frameworkDefinitions.map(f => [f.stableId || f._id || f.id, f.name]));
    }, [frameworkDefinitions]);

    const selectedFramework = useMemo(() => {
        if (!state.selectedFrameworkId) return null;
        return frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === state.selectedFrameworkId);
    }, [frameworkDefinitions, state.selectedFrameworkId]);

    const rulesForSelectedFramework = useMemo(() => {
        return selectedFramework?.rules || [];
    }, [selectedFramework]);

    const filteredRulesForPanel = useMemo(() => {
        if (!selectedFramework) return [];
        const searchTermLower = state.searchTerm.toLowerCase();
        // Ensure ruleDef.name exists before calling toLowerCase
        return rulesForSelectedFramework.filter(ruleDef =>
            ruleDef.name && ruleDef.name.toLowerCase().includes(searchTermLower)
        );
    }, [selectedFramework, rulesForSelectedFramework, state.searchTerm]);

    // Helper function to find the actual framework key used in selectedRules
    // Direct: custom = exact _id/id, predefined = stableId/type
    const getActualFrameworkKey = useCallback((framework) => {
        if (!framework) return null;

        const frameworkId = framework._id || framework.id;
        const stableId = framework.stableId || frameworkId || framework.type;

        // Direct matches first
        if (frameworkId && state.selectedRules[frameworkId]) {
            return frameworkId;
        }
        if (state.selectedRules[stableId]) {
            return stableId;
        }

        // Custom: exact id only, no fuzzy
        const isCustom = framework.type === 'custom' || String(framework.id || '').startsWith('custom-') || String(framework._id || '').startsWith('custom-') || framework.is_new;
        if (isCustom) {
            return frameworkId || stableId;
        }

        // Predefined: type is canonical (lazy: no name fuzzy)
        if (framework.type && state.selectedRules[framework.type]) {
            return framework.type;
        }
        if (framework.stableId && state.selectedRules[framework.stableId]) {
            return framework.stableId;
        }

        return stableId;
    }, [state.selectedRules, frameworkDefinitions]);

    // FIXED: Use cached framework names to preserve original French names
    const summaryDataForTable = useMemo(() => {
        const allRulesSummary = [];
        const businessValuesMap = new Map((currentBusinessValues || []).map(bv => [bv.id, { ...bv, supportAssets: (bv.supportAssets || []).map(asset => ({ ...asset })) }]));
        const dreadedEventsMap = new Map((currentDreadedEvents || []).map(de => [de.id, { ...de }]));

        // Iterate through framework definitions to maintain order
        frameworkDefinitions.forEach(framework => {
            const frameworkKey = getActualFrameworkKey(framework);
            const savedRules = state.selectedRules[frameworkKey];

            if (Array.isArray(savedRules) && savedRules.length > 0) {
                const frameworkName = state.frameworkNamesCache[frameworkKey] || framework.name;

                // Sort rules within this framework to match reference order
                const frameworkRulesOrder = (framework.rules || []).map(r => r.id);
                const sortedRules = [...savedRules].sort((a, b) => {
                    const indexA = frameworkRulesOrder.indexOf(a.id);
                    const indexB = frameworkRulesOrder.indexOf(b.id);
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                });

                sortedRules.forEach(savedRule => {
                    if (savedRule && savedRule.id) {
                        const associatedDeIds = state.ruleDreadedEvents[savedRule.id] || [];
                        const resolvedDesWithLinkedData = Array.isArray(associatedDeIds) ? associatedDeIds.map(deId => {
                            const deObject = dreadedEventsMap.get(deId);
                            if (!deObject) {
                                return null;
                            }

                            let linkedBusinessValue = null;
                            const linkedBvId = deObject.businessValue;
                            if (linkedBvId) {
                                const bvObject = businessValuesMap.get(String(linkedBvId));
                                if (bvObject) {
                                    linkedBusinessValue = { ...bvObject, supportAssets: bvObject.supportAssets || [] };
                                }
                            }
                            return { ...deObject, linkedBusinessValue };
                        }).filter(Boolean) : [];

                        allRulesSummary.push({
                            ...savedRule,
                            frameworkName,
                            frameworkId: frameworkKey,
                            dreadedEvents: resolvedDesWithLinkedData
                        });
                    }
                });
            }
        });

        return allRulesSummary;
    }, [state.selectedRules, state.ruleBusinessValues, state.ruleDreadedEvents, state.frameworkNamesCache, frameworkDefinitions, currentBusinessValues, currentDreadedEvents, getActualFrameworkKey]);

    // ponytail: radar chart data, 4 statuses only, NA (0) excluded
    const statusDistribution = useMemo(() => {
        const counts = { 4: 0, 3: 0, 2: 0, 1: 0 };
        summaryDataForTable.forEach(item => {
            const s = normalizeRuleStatus(item.status);
            if (s >= 1 && s <= 4) counts[s]++;
        });
        const labels = {
            4: t('securityFoundation.status.complete', 'Conforme'),
            3: t('securityFoundation.status.minorGaps', 'Écart mineur'),
            2: t('securityFoundation.status.majorGaps', 'Écart majeur'),
            1: t('securityFoundation.status.notEffective', 'Non applicable (N/A)')
        };
        const colors = { 4: '#22C55E', 3: '#EAB308', 2: '#EF4444', 1: '#9CA3AF' };
        return [4, 3, 2, 1].map(k => ({ status: labels[k], count: counts[k], color: colors[k] }));
    }, [summaryDataForTable, t]);

    // AUTO-SAVE: Disabled for now to prevent infinite loops
    // Users should use the manual save button
    // TODO: Re-implement auto-save with proper change detection


    // ENHANCED: Aggregate ALL selected rules from ALL frameworks for unified display
    // Direct from selectedRules so every R1 shows even if definition missing (lazy)
    const allSelectedRulesDetails = useMemo(() => {
        const allRules = [];
        const seenFrameworkKeys = new Set();

        // First, in definitions order for known frameworks
        frameworkDefinitions.forEach(framework => {
            const frameworkKey = getActualFrameworkKey(framework);
            if (!frameworkKey || seenFrameworkKeys.has(frameworkKey)) return;
            seenFrameworkKeys.add(frameworkKey);
            const savedRules = state.selectedRules[frameworkKey];
            if (Array.isArray(savedRules) && savedRules.length > 0) {
                const frameworkName = state.frameworkNamesCache[frameworkKey] || framework.name;
                const frameworkRulesOrder = (framework.rules || []).map(r => r.id);
                const sortedRules = [...savedRules].sort((a, b) => {
                    const indexA = frameworkRulesOrder.indexOf(a.id);
                    const indexB = frameworkRulesOrder.indexOf(b.id);
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                });
                sortedRules.forEach(rule => {
                    allRules.push({
                        ...rule,
                        frameworkKey,
                        frameworkName,
                        frameworkColor: getFrameworkColor(framework.type)
                    });
                });
            }
        });

        // Then, any orphan frameworkKeys in selectedRules not yet covered (definition missing)
        Object.entries(state.selectedRules).forEach(([frameworkKey, savedRules]) => {
            if (seenFrameworkKeys.has(frameworkKey)) return;
            if (!Array.isArray(savedRules) || savedRules.length === 0) return;
            const frameworkName = state.frameworkNamesCache[frameworkKey] || frameworkKey;
            const fw = frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === frameworkKey);
            const frameworkColor = getFrameworkColor(fw?.type);
            // No reference order for orphans, keep as stored
            savedRules.forEach(rule => {
                allRules.push({
                    ...rule,
                    frameworkKey,
                    frameworkName,
                    frameworkColor
                });
            });
        });

        return allRules;
    }, [state.selectedRules, state.frameworkNamesCache, frameworkDefinitions, getActualFrameworkKey]);

    // Keep the original selectedRulesDetails for backward compatibility (used in other places)
    const selectedRulesDetails = useMemo(() => {
        if (!state.selectedFrameworkId) {
            return [];
        }

        const selectedFramework = frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === state.selectedFrameworkId);
        const actualKey = getActualFrameworkKey(selectedFramework);

        if (actualKey && state.selectedRules[actualKey]) {
            return state.selectedRules[actualKey];
        }

        const allRulesKeys = Object.keys(state.selectedRules).filter(key => state.selectedRules[key].length > 0);

        if (allRulesKeys.length === 1) {
            return state.selectedRules[allRulesKeys[0]];
        }

        return [];
    }, [state.selectedFrameworkId, state.selectedRules, frameworkDefinitions, getActualFrameworkKey]);


    // --- Callback Functions ---
    const handleSelectFramework = useCallback((frameworkId) => {
        if (state.selectedFrameworkId === frameworkId) {
            return;
        }

        const framework = frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === frameworkId);
        const keyToUse = framework ? (framework.stableId || framework._id || framework.id) : frameworkId;
        console.log(`[SecurityFramework] Selecting framework: ${keyToUse}`);
        dispatch({ type: 'SELECT_FRAMEWORK', payload: keyToUse });
    }, [dispatch, state.selectedFrameworkId, frameworkDefinitions]);

    const handleToggleRule = useCallback((frameworkActualId, ruleDefinition) => {
        dispatch({ type: 'TOGGLE_RULE', payload: { frameworkId: frameworkActualId, rule: ruleDefinition } });
    }, [dispatch]);

    const handleRuleChange = useCallback((frameworkActualId, ruleId, field, value) => {
        dispatch({ type: 'UPDATE_RULE', payload: { frameworkId: frameworkActualId, ruleId, field, value } });
    }, [dispatch]);

    // These don't depend on framework ID directly
    const handleUpdateRuleBusinessValues = useCallback((ruleId, businessValueIds) => { dispatch({ type: 'UPDATE_RULE_BVS', payload: { ruleId, businessValueIds } }); }, [dispatch]);
    const handleUpdateRuleDreadedEvents = useCallback((ruleId, dreadedEventIds) => { dispatch({ type: 'UPDATE_RULE_DES', payload: { ruleId, dreadedEventIds } }); }, [dispatch]);
    const handleUpdateRuleGapComment = useCallback((ruleId, comment) => { dispatch({ type: 'UPDATE_RULE_GAP_COMMENT', payload: { ruleId, comment } }); }, [dispatch]);

    const handleCreateDreadedEvent = useCallback(async (name, description, businessValueId, securityPillar, severity, severityJustification, impacts) => {
        const currentDEs = currentDreadedEvents || [];
        const newId = `de-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
        const newDE = {
            id: newId,
            name,
            description: description || '',
            securityPillar: securityPillar || 'other',
            severity: severity || 'moderate',
            businessValue: businessValueId,
            impacts: impacts || [],
            severityJustification: severityJustification || ''
        };
        const updatedDEs = [...currentDEs, newDE];
        const response = await useAtelier1Store.getState().saveDreadedEvents(updatedDEs);
        if (response && response.success) {
            return newDE;
        }
        throw new Error('Failed to create dreaded event');
    }, [currentDreadedEvents]);

    const handleSearchTermChange = useCallback((e) => { dispatch({ type: 'SET_SEARCH_TERM', payload: e.target.value }); }, [dispatch]);

    // Modify Rule Handler - Ensure framework selection uses correct ID
    const handleModifyRule = useCallback((ruleIdToModify) => {
        let frameworkDbOrTempId = null;
        let ruleToModify = null;

        for (const [fwId, rules] of Object.entries(state.selectedRules)) {
            const foundRule = rules.find(r => r.id === ruleIdToModify);
            if (foundRule) {
                frameworkDbOrTempId = fwId;
                ruleToModify = foundRule;
                break;
            }
        }

        if (!frameworkDbOrTempId || !ruleToModify) {
            alert(t('securityFoundation.status.ruleNotFoundForModification'));
            return;
        }

        if (state.selectedFrameworkId !== frameworkDbOrTempId) {
            dispatch({ type: 'SELECT_FRAMEWORK', payload: frameworkDbOrTempId });
        }

        setRuleIdToScrollTo(ruleIdToModify);

    }, [state.selectedRules, state.selectedFrameworkId, dispatch, t]);

    const handleDeleteRule = useCallback((ruleIdToDelete) => {
        // Find the framework and the specific rule object
        let frameworkId = null;
        let ruleToDelete = null;

        for (const [fwId, rules] of Object.entries(state.selectedRules)) {
            const foundRule = rules.find(r => r.id === ruleIdToDelete);
            if (foundRule) {
                frameworkId = fwId;
                ruleToDelete = foundRule;
                break;
            }
        }

        if (!frameworkId || !ruleToDelete) {
            alert(t('securityFoundation.status.ruleNotFound'));
            return;
        }

        // Open the custom confirmation modal instead of window.confirm
        setRuleToDeleteData({ frameworkId, ruleToDelete });
        setIsDeleteModalOpen(true);
    }, [state.selectedRules, t]);

    const confirmDelete = useCallback(() => {
        if (!ruleToDeleteData) return;

        const { frameworkId, ruleToDelete } = ruleToDeleteData;

        // Dispatch the existing TOGGLE_RULE action. The reducer handles removal.
        dispatch({
            type: 'TOGGLE_RULE',
            payload: {
                frameworkId: frameworkId,
                rule: ruleToDelete
            }
        });

        // Mark as modified for auto-save
        markModified();

        // Close modal
        setRuleToDeleteData(null);
        setIsDeleteModalOpen(false);
    }, [ruleToDeleteData, dispatch, markModified]);

    const confirmDeselect = useCallback(() => {
        if (!deselectConfirmData) return;

        const { type, frameworkId, rules, allRulesToToggle } = deselectConfirmData;

        if (type === 'single') {
            dispatch({ type: 'TOGGLE_RULE', payload: { frameworkId, rule: rules[0] } });
        } else if (type === 'multiple' && allRulesToToggle) {
            const currentSelectedRuleIds = new Set((state.selectedRules[frameworkId] || []).map(r => r.id));
            allRulesToToggle.forEach(ruleDef => {
                if (currentSelectedRuleIds.has(ruleDef.id)) {
                    dispatch({ type: 'TOGGLE_RULE', payload: { frameworkId, rule: ruleDef } });
                }
            });
        }

        markModified();
        setDeselectConfirmData(null);
    }, [deselectConfirmData, dispatch, state.selectedRules, markModified]);

    const handleDeleteFramework = useCallback((frameworkId, frameworkName) => {
        const fw = state.frameworkDefinitions.find(f => (f._id || f.id) === frameworkId || (f.stableId || f._id || f.id) === frameworkId);
        const isNew = !!fw?.is_new || String(frameworkId).startsWith('custom-');
        const count = (state.selectedRules[frameworkId] || state.selectedRules[fw?.stableId || fw?._id || fw?.id] || []).length;
        let actualKey = frameworkId;
        try { if (fw) actualKey = getActualFrameworkKey(fw) || frameworkId; } catch {}
        const actualCount = (state.selectedRules[actualKey] || []).length || count;
        setFrameworkToDelete({ id: frameworkId, name: frameworkName, isNew, count: actualCount });
        setIsFrameworkDeleteModalOpen(true);
    }, [state.frameworkDefinitions, state.selectedRules, getActualFrameworkKey]);

    const confirmDeleteFramework = useCallback(async () => {
        if (!frameworkToDelete) return;
        const { id, isNew } = frameworkToDelete;
        // Capture deleted ruleIds BEFORE dispatch (state.selectedRules still has old entry)
        const fwBefore = state.frameworkDefinitions.find(f => (f._id || f.id) === frameworkToDelete.id);
        const keyBefore = fwBefore ? (getActualFrameworkKey(fwBefore) || frameworkToDelete.id) : frameworkToDelete.id;
        const deletedRules = state.selectedRules[keyBefore] || state.selectedRules[frameworkToDelete.id] || [];
        const deletedRuleIds = new Set(deletedRules.map(r => String(r.id)));
        const newSelectedRulesAfterDelete = { ...state.selectedRules };
        delete newSelectedRulesAfterDelete[keyBefore];
        delete newSelectedRulesAfterDelete[frameworkToDelete.id];
        // optimistic local delete first
        dispatch({ type: 'DELETE_CUSTOM_FRAMEWORK', payload: { frameworkId: id } });
        markModified();
        // Purge controlPlan entries for deleted framework's rules
        try {
            const atelierStore = useAtelier1Store.getState();
            const currentPlan = atelierStore.controlPlan;
            if (currentPlan?.planData?.plan && deletedRuleIds.size > 0) {
                const cleaned = atelierStore.cleanupControlPlan(currentPlan, { selectedRules: newSelectedRulesAfterDelete });
                if (cleaned.wasModified) {
                    // update local store immediately
                    useAtelier1Store.setState({ controlPlan: cleaned.planData });
                    // try to persist silently (no toast)
                    try { await atelierStore.saveControlPlan(cleaned.planData.planData, broadcastUpdate); } catch {}
                }
            }
        } catch {}
        // Build cleaned maps for securityFramework persist
        const newFrameworkNamesCacheAfterDelete = { ...state.frameworkNamesCache };
        delete newFrameworkNamesCacheAfterDelete[keyBefore];
        delete newFrameworkNamesCacheAfterDelete[frameworkToDelete.id];
        const newRuleBVsAfterDelete = { ...state.ruleBusinessValues };
        const newRuleDEsAfterDelete = { ...state.ruleDreadedEvents };
        const newRuleGapAfterDelete = { ...state.ruleGapComments };
        deletedRuleIds.forEach(rid => { delete newRuleBVsAfterDelete[rid]; delete newRuleDEsAfterDelete[rid]; delete newRuleGapAfterDelete[rid]; });

        // Optimistic store update + backend persist (no toast, silent)
        try {
            const newSecurityData = {
                selectedRules: newSelectedRulesAfterDelete,
                ruleBusinessValues: newRuleBVsAfterDelete,
                ruleDreadedEvents: newRuleDEsAfterDelete,
                ruleGapComments: newRuleGapAfterDelete,
                frameworkNamesCache: newFrameworkNamesCacheAfterDelete,
                summary: []
            };
            // update local reducer already done via dispatch, now update atelier store immediately
            // Use atelierStore to persist
            const atelierStore2 = useAtelier1Store.getState();
            // optimistic set to keep UI and Controls in sync before API returns
            // Keep definitions as is (frameworkDefinitions already updated via dispatch, but store's securityFramework.definitions not yet)
            // We will let saveSecurityFramework set securityFramework with new data
            await atelierStore2.saveSecurityFramework(newSecurityData, null, broadcastUpdate);
        } catch {}
        setIsFrameworkDeleteModalOpen(false);
        setFrameworkToDelete(null);
        if (!isNew) {
            try {
                const res = await frameworkDefinitionService.deleteFrameworkDefinition(id);
                if (!res.success) {
                    showErrorToast(res.message || 'Erreur suppression');
                } else {
                    showSuccessToast(t('securityFoundation.status.frameworkDeleted', 'Référentiel supprimé'));
                }
            } catch (e) { showErrorToast(e.message); }
        } else {
            showSuccessToast(t('securityFoundation.status.frameworkDeleted', 'Référentiel supprimé'));
        }
    }, [frameworkToDelete, state.frameworkDefinitions, state.selectedRules, getActualFrameworkKey, dispatch, markModified, broadcastUpdate, t]);

    const handleRenameFramework = useCallback(async (frameworkId, newName) => {
        const trimmed = newName.trim();
        if (!trimmed) return;
        const fw = state.frameworkDefinitions.find(f => (f._id || f.id) === frameworkId);
        const isNew = !!fw?.is_new || String(frameworkId).startsWith('custom-');
        dispatch({ type: 'RENAME_CUSTOM_FRAMEWORK', payload: { frameworkId: frameworkId, newName: trimmed } });
        markModified();
        if (!isNew) {
            try {
                const res = await frameworkDefinitionService.updateFrameworkDefinition(frameworkId, { name: trimmed });
                if (!res.success) {
                    showErrorToast(res.message || 'Erreur renommage');
                } else {
                    showSuccessToast(t('securityFoundation.status.frameworkRenamed', 'Référentiel renommé'));
                }
            } catch (e) { showErrorToast(e.message); }
        } else {
            showSuccessToast(t('securityFoundation.status.frameworkRenamed', 'Référentiel renommé'));
        }
    }, [state.frameworkDefinitions, dispatch, markModified, t]);

    // Global AI Matching Handler - Shows Rule Selection Modal First
    const handleGlobalAIMatching = useCallback(() => {
        if (isGlobalAIMatching || !currentDreadedEvents || currentDreadedEvents.length === 0) {
            return;
        }

        // Find all unmatched rules
        const unmatchedRules = allSelectedRulesDetails.filter(rule => {
            const linkedEvents = state.ruleDreadedEvents[rule.id] || [];
            return linkedEvents.length === 0;
        });

        if (unmatchedRules.length === 0) {
            showInfoToast(t('securityFoundation.ai.allRulesMatched', 'Toutes les règles sont déjà associées à des événements redoutés'));
            return;
        }

        // Open selecting modal instead of immediately making requests (start with no pre-selected rules)
        setSelectedRulesForAi([]);
        setShowAiRuleSelectionModal(true);
    }, [isGlobalAIMatching, allSelectedRulesDetails, currentDreadedEvents, state.ruleDreadedEvents, t]);

    // Handle starting batch matching after rules are selected
    const handleStartBatchAIMatching = useCallback(async () => {
        if (selectedRulesForAi.length === 0) return;
        
        setShowAiRuleSelectionModal(false);
        setIsGlobalAIMatching(true);
        setShowAiProgressModal(true);
        abortControllerRef.current = new AbortController();
        const toastId = showLoadingToast(t('securityFoundation.ai.matchingRules', { count: selectedRulesForAi.length }));

        try {
            const rulesToProcess = allSelectedRulesDetails.filter(r => selectedRulesForAi.includes(r.id));

            const dreadedEventsFormatted = currentDreadedEvents.map(event => ({
                id: event.id,
                name: event.name,
                description: event.description || '',
                severity: event.severity || '',
                securityPillar: event.securityPillar || ''
            }));

            const context = {
                rules: rulesToProcess,
                dreadedEvents: dreadedEventsFormatted,
                analysisId: currentAnalysis?.id
            };

            const result = await api.post('/ai/match-rules-batch', context, {
                signal: abortControllerRef.current?.signal
            });

            const allMatches = (result.success && result.data && result.data.matches) ? result.data.matches : {};

            const suggestions = [];
            Object.entries(allMatches).forEach(([ruleId, matches]) => {
                if (matches && matches.length > 0) {
                     const ruleDetails = rulesToProcess.find(r => r.id === ruleId);
                     if (ruleDetails) {
                         const matchedEventIds = matches.map(m => String(m.dreadedEventId));
                         const matchedEventsObjects = matchedEventIds.map(eventId => {
                            const event = currentDreadedEvents.find(e => String(e.id) === eventId);
                            return event || null;
                        }).filter(Boolean);

                         suggestions.push({
                            rule: ruleDetails,
                            matchedEvents: matchedEventsObjects,
                            matchedEventIds: matchedEventIds
                        });
                     }
                }
            });

            updateToast(toastId, '', 'success');

            if (suggestions.length > 0) {
                setAiMatchingSuggestions(suggestions);
                setShowAiMatchingModal(true);
            } else {
                showInfoToast(t('securityFoundation.ai.noSuggestionsFound', 'Aucune suggestion trouvée pour les règles non associées'));
            }

        } catch (error) {
            if (error.name === 'CanceledError' || error.message?.includes('canceled')) {
                console.log('AI matching was canceled by the user');
                updateToast(toastId, t('securityFoundation.ai.matchingCanceled', 'Analyse annulée par l\'utilisateur'), 'info');
            } else {
                console.error('Error during global AI matching:', error);
                handleAIError(error, { language: 'fr' });
                updateToast(
                    toastId,
                    t('securityFoundation.ai.matchingError', 'Erreur lors de l\'analyse automatique'),
                    'error'
                );
            }
        } finally {
            setIsGlobalAIMatching(false);
            setShowAiProgressModal(false);
        }
    }, [selectedRulesForAi, allSelectedRulesDetails, currentDreadedEvents, currentAnalysis, t]);



    // Handler to accept all AI suggestions
    const handleAcceptAllSuggestions = useCallback(() => {
        let acceptedCount = 0;

        aiMatchingSuggestions.forEach(suggestion => {
            // Update the rule with matched dreaded events
            handleUpdateRuleDreadedEvents(suggestion.rule.id, suggestion.matchedEventIds);

            // Also update business values based on the matched events
            const derivedBvIds = suggestion.matchedEventIds.reduce((acc, deId) => {
                const event = currentDreadedEvents.find(e => String(e.id) === deId);
                if (event && event.businessValue) {
                    acc.add(event.businessValue);
                }
                return acc;
            }, new Set());

            if (derivedBvIds.size > 0) {
                handleUpdateRuleBusinessValues(suggestion.rule.id, Array.from(derivedBvIds));
            }

            acceptedCount++;
        });

        // Close modal and show success message
        setShowAiMatchingModal(false);
        setAiMatchingSuggestions([]);
        showSuccessToast(t('securityFoundation.ai.suggestionsAccepted', `${acceptedCount} association(s) acceptée(s)`));
    }, [aiMatchingSuggestions, currentDreadedEvents, handleUpdateRuleDreadedEvents, handleUpdateRuleBusinessValues, t]);

    // Handler to accept a single suggestion
    const handleAcceptSuggestion = useCallback((suggestionIndex) => {
        const suggestion = aiMatchingSuggestions[suggestionIndex];

        if (suggestion) {
            // Update the rule with matched dreaded events
            handleUpdateRuleDreadedEvents(suggestion.rule.id, suggestion.matchedEventIds);

            // Also update business values based on the matched events
            const derivedBvIds = suggestion.matchedEventIds.reduce((acc, deId) => {
                const event = currentDreadedEvents.find(e => String(e.id) === deId);
                if (event && event.businessValue) {
                    acc.add(event.businessValue);
                }
                return acc;
            }, new Set());

            if (derivedBvIds.size > 0) {
                handleUpdateRuleBusinessValues(suggestion.rule.id, Array.from(derivedBvIds));
            }

            // Remove this suggestion from the list
            const updatedSuggestions = aiMatchingSuggestions.filter((_, index) => index !== suggestionIndex);
            setAiMatchingSuggestions(updatedSuggestions);

            // If no more suggestions, close the modal
            if (updatedSuggestions.length === 0) {
                setShowAiMatchingModal(false);
                showSuccessToast(t('securityFoundation.ai.allSuggestionsProcessed', 'Toutes les suggestions ont été traitées'));
            } else {
                showSuccessToast(t('securityFoundation.ai.suggestionAccepted', 'Association acceptée'));
            }
        }
    }, [aiMatchingSuggestions, currentDreadedEvents, handleUpdateRuleDreadedEvents, handleUpdateRuleBusinessValues, t]);

    // Handler to reject a single suggestion
    const handleRejectSuggestion = useCallback((suggestionIndex) => {
        // Remove this suggestion from the list
        const updatedSuggestions = aiMatchingSuggestions.filter((_, index) => index !== suggestionIndex);
        setAiMatchingSuggestions(updatedSuggestions);

        // If no more suggestions, close the modal
        if (updatedSuggestions.length === 0) {
            setShowAiMatchingModal(false);
            showInfoToast(t('securityFoundation.ai.allSuggestionsRejected', 'Toutes les suggestions ont été rejetées'));
        }
    }, [aiMatchingSuggestions, t]);

    // Handler to close the modal without accepting
    const handleCloseAiMatchingModal = useCallback(() => {
        setShowAiMatchingModal(false);
        setAiMatchingSuggestions([]);
    }, []);

    // FIXED: Select All Toggle Handler using the helper function
    const handleSelectAllToggle = useCallback(() => {
        if (!selectedFramework) return;

        const actualFrameworkKey = getActualFrameworkKey(selectedFramework);
        const currentRules = state.selectedRules[actualFrameworkKey] || [];
        const currentSelectedRuleIds = new Set(currentRules.map(r => r.id));

        const allVisibleSelected = filteredRulesForPanel.length > 0 &&
            filteredRulesForPanel.every(rule => currentSelectedRuleIds.has(rule.id));

        if (allVisibleSelected) {
            filteredRulesForPanel.forEach(ruleDef => {
                if (currentSelectedRuleIds.has(ruleDef.id)) {
                    dispatch({ type: 'TOGGLE_RULE', payload: { frameworkId: actualFrameworkKey, rule: ruleDef } });
                }
            });
        } else {
            // Select all visible rules that are not already selected
            filteredRulesForPanel.forEach(ruleDef => {
                if (!currentSelectedRuleIds.has(ruleDef.id)) {
                    dispatch({ type: 'TOGGLE_RULE', payload: { frameworkId: actualFrameworkKey, rule: ruleDef } });
                }
            });
        }
    }, [selectedFramework, getActualFrameworkKey, state.selectedRules, filteredRulesForPanel, dispatch]);

    // REWRITTEN Save Handler
    const handleSaveSecurityData = useCallback(async () => {
        if (!currentAnalysis?.id) {
            showErrorToast(t('securityFoundation.status.noAnalysisSelected'));
            return;
        }
        dispatch({ type: 'SAVE_START' });

        // Show loading toast
        const toastId = showLoadingToast(t('securityFoundation.status.savingSecurityFoundation'));

        let definitionsSaved = false;
        let updatedDefinitionsList = [...state.frameworkDefinitions]; // Start with current list
        let tempIdToSavedDataMapManual = new Map();
        let migratedSelectedRules = null;
        let migratedFrameworkNamesCache = null;
        let migratedSelectedFrameworkId = state.selectedFrameworkId;

        try {
            // Step A: Save newly added framework definitions
            const newDefinitionsToSave = state.frameworkDefinitions.filter(f => f.is_new);

            if (newDefinitionsToSave.length > 0) {
                console.log(`Attempting to save ${newDefinitionsToSave.length} new framework definition(s)...`);
                const savePromises = newDefinitionsToSave.map(def => {
                    const payload = {
                        name: def.name,
                        description: def.description,
                        rules: def.rules,
                        analysisId: currentAnalysis.id
                    };
                    return frameworkDefinitionService.createFrameworkDefinition(payload);
                });

                const results = await Promise.allSettled(savePromises);

                const failedSaves = results.filter(r => r.status === 'rejected' || !r.value?.success);
                if (failedSaves.length > 0) {
                    console.error('Failed to save some framework definitions:', failedSaves);
                    let errorMsg = failedSaves[0].reason?.message || failedSaves[0].value?.message || t('securityFoundation.status.saveDefinitionsFailed');
                    // Show simple message for validation errors
                    if (errorMsg.includes('Rule name is required') || errorMsg.includes('validation failed')) {
                        errorMsg = t('securityFoundation.securityframeworksaveissue.invalidRules') || 'Erreur lors de la sauvegarde. Vérifiez les champs saisis.';
                    }
                    throw new Error(errorMsg); // Throw error to stop proceeding
                }

                // All definitions saved successfully, update local state
                console.log("New definitions saved successfully. Updating local state...");
                const savedDefinitionData = results.map(r => r.value.data); // Extract saved data with DB _id

                // Create a map of tempId -> savedData
                tempIdToSavedDataMapManual = new Map();
                newDefinitionsToSave.forEach((tempDef, index) => {
                    if (savedDefinitionData[index]) {
                        tempIdToSavedDataMapManual.set(tempDef.id, savedDefinitionData[index]);
                    }
                });

                // Update the main list: replace temp items with saved ones (remove is_new)
                updatedDefinitionsList = state.frameworkDefinitions.map(f => {
                    if (f.is_new && tempIdToSavedDataMapManual.has(f.id)) {
                        const savedData = tempIdToSavedDataMapManual.get(f.id);
                        // Return the saved data from API (it has _id), ensure no is_new flag
                        const { is_new, ...rest } = savedData;
                        return rest;
                    }
                    // If not new or save failed somehow (shouldn't happen if checks passed), return as is
                    // Make sure existing saved items don't have is_new flag either
                    const { is_new, ...rest } = f;
                    return rest;
                });

                // BUGFIX: migrate selectedRules/frameworkNamesCache from temp IDs to persisted DB _ids
                // Without this, backend stores dead temp keys and second custom overwrites first on reload
                if (tempIdToSavedDataMapManual.size > 0) {
                    migratedSelectedRules = { ...state.selectedRules };
                    migratedFrameworkNamesCache = { ...state.frameworkNamesCache };
                    tempIdToSavedDataMapManual.forEach((savedData, tempId) => {
                        const newKey = savedData._id || savedData.id;
                        if (newKey && newKey !== tempId) {
                            if (migratedSelectedRules[tempId]) {
                                migratedSelectedRules[newKey] = migratedSelectedRules[tempId];
                                delete migratedSelectedRules[tempId];
                            }
                            if (migratedFrameworkNamesCache[tempId]) {
                                migratedFrameworkNamesCache[newKey] = migratedFrameworkNamesCache[tempId];
                                delete migratedFrameworkNamesCache[tempId];
                            }
                        }
                    });
                    if (tempIdToSavedDataMapManual.has(state.selectedFrameworkId)) {
                        const saved = tempIdToSavedDataMapManual.get(state.selectedFrameworkId);
                        migratedSelectedFrameworkId = saved._id || saved.id;
                    }
                    dispatch({ type: 'UPDATE_SAVED_DEFINITIONS', payload: { updatedDefinitions: updatedDefinitionsList } });
                    dispatch({ type: 'APPLY_MIGRATION', payload: { selectedRules: migratedSelectedRules, frameworkNamesCache: migratedFrameworkNamesCache, selectedFrameworkId: migratedSelectedFrameworkId } });
                } else {
                    dispatch({ type: 'UPDATE_SAVED_DEFINITIONS', payload: { updatedDefinitions: updatedDefinitionsList } });
                }
                definitionsSaved = true; // Mark definitions as saved
            } else {
                console.log("No new framework definitions to save.");
                definitionsSaved = true; // Nothing to save, so conceptually successful
            }

            // Step B: Save selections (only if definitions saved or none needed saving)
            if (definitionsSaved) {
                console.log("Proceeding to save selections...");
                const selectedRulesToSave = migratedSelectedRules || state.selectedRules;
                const frameworkNamesCacheToSave = migratedFrameworkNamesCache || state.frameworkNamesCache;
                const selectionData = {
                    selectedRules: selectedRulesToSave,
                    ruleBusinessValues: state.ruleBusinessValues,
                    ruleDreadedEvents: state.ruleDreadedEvents,
                    ruleGapComments: state.ruleGapComments,
                    frameworkNamesCache: frameworkNamesCacheToSave,
                    summary: summaryDataForTable
                };

                // Call the context function to save selections to AnalysisComponent
                const selectionResponse = await useAtelier1Store.getState().saveSecurityFramework(selectionData, null, broadcastUpdate);

                if (selectionResponse && selectionResponse.success) {
                    console.log("Selections saved successfully via context.");
                    dispatch({ type: 'SAVE_SUCCESS' });

                    // Update loading toast to success
                    updateToast(toastId, t('securityFoundation.status.saveSuccess'), 'success');
                } else {
                    const errorMsg = selectionResponse?.message || t('securityFoundation.status.saveSelectionsFailed');
                    console.error("Save selections failed:", selectionResponse);
                    throw new Error(errorMsg); // Throw to be caught below
                }
            }

        } catch (error) {
            console.error('Error during save process:', error);
            dispatch({ type: 'SAVE_ERROR', payload: error.message || 'Une erreur est survenue lors de la sauvegarde.' });

            // Update loading toast to error
            updateToast(toastId, `${t('securityFoundation.status.saveErrorPrefix')}: ${error.message || t('securityFoundation.status.saveErrorGeneric')}`, 'error');
        }
    }, [
        currentAnalysis?.id,
        state.frameworkDefinitions,
        state.selectedRules,
        state.ruleBusinessValues,
        state.ruleDreadedEvents,
        broadcastUpdate,
        summaryDataForTable,
        dispatch
    ]);

    // Silent save function for auto-save (no toast notifications)
    // GUARDED: Will only save if data was loaded AND user made modifications
    const handleSilentSave = async () => {
        if (!currentAnalysis?.id) return false;

        // ⛔ Safety guard: don't save if data hasn't loaded or user hasn't changed anything
        if (!canAutoSave()) {
            return 'skipped'; // Signal to parent that no save was needed
        }

        let definitionsSaved = false;
        let tempIdToSavedDataMapSilent = new Map();
        let migratedSelectedRulesSilent = null;
        let migratedFrameworkNamesCacheSilent = null;

        try {
            // Step A: Save newly added framework definitions
            const newDefinitionsToSave = state.frameworkDefinitions.filter(f => f.is_new);

            if (newDefinitionsToSave.length > 0) {
                const savePromises = newDefinitionsToSave.map(def => {
                    const payload = {
                        name: def.name,
                        description: def.description,
                        rules: def.rules,
                        analysisId: currentAnalysis.id
                    };
                    return frameworkDefinitionService.createFrameworkDefinition(payload);
                });

                const results = await Promise.allSettled(savePromises);
                const failedSaves = results.filter(r => r.status === 'rejected' || !r.value?.success);

                if (failedSaves.length > 0) return false;

                const savedDefinitionData = results.map(r => r.value.data);
                tempIdToSavedDataMapSilent = new Map();
                newDefinitionsToSave.forEach((tempDef, index) => {
                    if (savedDefinitionData[index]) {
                        tempIdToSavedDataMapSilent.set(tempDef.id, savedDefinitionData[index]);
                    }
                });

                const updatedDefinitionsList = state.frameworkDefinitions.map(f => {
                    if (f.is_new && tempIdToSavedDataMapSilent.has(f.id)) {
                        const savedData = tempIdToSavedDataMapSilent.get(f.id);
                        const { is_new, ...rest } = savedData;
                        return rest;
                    }
                    const { is_new, ...rest } = f;
                    return rest;
                });

                // BUGFIX: migrate selectedRules/cache keys from temp to DB ids (same as manual save)
                if (tempIdToSavedDataMapSilent.size > 0) {
                    migratedSelectedRulesSilent = { ...state.selectedRules };
                    migratedFrameworkNamesCacheSilent = { ...state.frameworkNamesCache };
                    tempIdToSavedDataMapSilent.forEach((savedData, tempId) => {
                        const newKey = savedData._id || savedData.id;
                        if (newKey && newKey !== tempId) {
                            if (migratedSelectedRulesSilent[tempId]) {
                                migratedSelectedRulesSilent[newKey] = migratedSelectedRulesSilent[tempId];
                                delete migratedSelectedRulesSilent[tempId];
                            }
                            if (migratedFrameworkNamesCacheSilent[tempId]) {
                                migratedFrameworkNamesCacheSilent[newKey] = migratedFrameworkNamesCacheSilent[tempId];
                                delete migratedFrameworkNamesCacheSilent[tempId];
                            }
                        }
                    });
                    dispatch({ type: 'UPDATE_SAVED_DEFINITIONS', payload: { updatedDefinitions: updatedDefinitionsList } });
                    dispatch({ type: 'APPLY_MIGRATION', payload: { selectedRules: migratedSelectedRulesSilent, frameworkNamesCache: migratedFrameworkNamesCacheSilent, selectedFrameworkId: state.selectedFrameworkId } });
                } else {
                    dispatch({ type: 'UPDATE_SAVED_DEFINITIONS', payload: { updatedDefinitions: updatedDefinitionsList } });
                }
                definitionsSaved = true;
            } else {
                definitionsSaved = true;
            }

            // Step B: Save selections
            if (definitionsSaved) {
                const selectedRulesToSave = migratedSelectedRulesSilent || state.selectedRules;
                const frameworkNamesCacheToSave = migratedFrameworkNamesCacheSilent || state.frameworkNamesCache;
                const selectionData = {
                    selectedRules: selectedRulesToSave,
                    ruleBusinessValues: state.ruleBusinessValues,
                    ruleDreadedEvents: state.ruleDreadedEvents,
                    ruleGapComments: state.ruleGapComments,
                    frameworkNamesCache: frameworkNamesCacheToSave,
                    summary: summaryDataForTable
                };

                const selectionResponse = await useAtelier1Store.getState().saveSecurityFramework(selectionData, null, broadcastUpdate);

                if (selectionResponse && selectionResponse.success) {
                    dispatch({ type: 'SAVE_SUCCESS' });
                    clearModified();
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('Silent save error security framework:', error);
            return false;
        }
    };

    useSilentSaveListener('security', handleSilentSave);

    // Handlers for CSV import
    const handleFileUpload = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv'];
        const allowedMimeTypes = ['text/csv', 'application/vnd.ms-excel', 'application/csv'];
        const fileName = file.name || '';
        const fileExt = fileName.split('.').pop()?.toLowerCase();
        const fileMime = file.type || '';

        if (!fileExt || !allowedExtensions.includes('.' + fileExt)) {
            dispatch({ type: 'SET_IMPORT_ERROR', payload: t('securityFoundation.import.invalidExtension') || 'Seuls les fichiers CSV sont acceptés' });
            return;
        }

        if (fileMime && !allowedMimeTypes.includes(fileMime)) {
            dispatch({ type: 'SET_IMPORT_ERROR', payload: t('securityFoundation.import.invalidMimeType') || 'Type de fichier non valide' });
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsedData = parseCSV(e.target.result);
                if (parsedData.length === 0) {
                    dispatch({ type: 'SET_IMPORT_ERROR', payload: t('securityFoundation.import.emptyFile') });
                    return;
                }
                // Reset custom name when opening modal
                setCustomFrameworkName(t('securityFoundation.import.defaultFrameworkName') + ' ' + (state.frameworkDefinitions.filter(f => !f.is_predefined).length + 1));
                dispatch({ type: 'SET_CSV_DATA', payload: parsedData });
                dispatch({ type: 'SHOW_IMPORT_MODAL' }); // Corrected: pass payload: true is not needed
            } catch (error) {
                dispatch({ type: 'SET_IMPORT_ERROR', payload: t('securityFoundation.import.parseError') });
            }
        };
        reader.onerror = () => {
            dispatch({ type: 'SET_IMPORT_ERROR', payload: t('securityFoundation.import.readError') });
        };
        reader.readAsText(file);
        // Clear the input value so the same file can be selected again
        event.target.value = null;
    }, [dispatch, state.frameworkDefinitions]); // Added state.frameworkDefinitions

    // UPDATED: Use customFrameworkName state
    const handleImportConfirm = useCallback(() => {
        if (!state.csvImportData || !customFrameworkName.trim()) {
            showErrorToast(t('securityFoundation.status.specifyFrameworkName'));
            return;
        }

        const temporaryId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        const customFramework = {
            id: temporaryId, // Use temporary frontend ID
            name: customFrameworkName.trim(),
            description: `${t('securityFoundation.status.importedOn')} ${new Date().toLocaleDateString()}`,
            is_predefined: false,
            type: 'custom',
            rules: state.csvImportData.map(rule => ({ ...rule, id: rule.id || `rule-${Date.now()}-${Math.random().toString(36).substr(2, 5)}` })) // Ensure rules also have temp IDs
        };

        // Dispatch to add locally with is_new flag
        dispatch({ type: 'ADD_CUSTOM_FRAMEWORK', payload: { framework: customFramework } });

        // Show success toast
        showSuccessToast(t('securityFoundation.status.frameworkImported', { name: customFrameworkName.trim(), count: customFramework.rules.length }));
    }, [state.csvImportData, customFrameworkName, dispatch]);

    // NEW: Function to trigger file input click
    const handleImportClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    // NEW: Print function for summary table (UPDATED)
    const printTable = useCallback(() => {
        const printArea = document.querySelector('.summary-table-print-area');
        if (!printArea) {
            console.error("Print area '.summary-table-print-area' not found.");
            showErrorToast(t('securityFoundation.status.printAreaNotFound'));
            return;
        }
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<html><head><title>${t('securityFoundation.sections.summary')}</title>`);
        // Print styles remain the same
        printWindow.document.write(`
        <style>
            body { font-family: sans-serif; }
            table { border-collapse: collapse; width: 100%; font-size: 9pt; margin-bottom: 20px; }
            th, td { border: 1px solid #ccc; padding: 6px; text-align: left; vertical-align: top; word-wrap: break-word; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .no-print { display: none; }
            h3 { font-size: 14pt; margin-bottom: 15px; }
            /* Attempt to style severity squares for print */
            .severity-square { padding: 4px; border-radius: 4px; font-size: 8pt; text-align: center; color: black; border: 1px solid #ddd; margin-bottom: 2px; }
            .bg-black { background-color: #000 !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .bg-red-600 { background-color: #DC2626 !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .bg-yellow-500 { background-color: #F59E0B !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .bg-yellow-200 { background-color: #FEF08A !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .bg-cyan-300 { background-color: #67E8F9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .bg-gray-200 { background-color: #E5E7EB !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .text-white { color: #fff !important; }
            .text-black { color: #000 !important; }
        </style>
    `);
        printWindow.document.write('</head><body>');
        printWindow.document.write(`<h3>${t('securityFoundation.sections.summary')}</h3>`);

        // Clone the table to avoid modifying the original
        const tableClone = printArea.querySelector('table')?.cloneNode(true);
        if (tableClone) {
            // Remove the last header (Actions)
            const headers = tableClone.querySelectorAll('thead th');
            if (headers.length > 0) {
                headers[headers.length - 1].remove();
            }

            // Remove the last cell (Actions) from each body row
            const bodyRows = tableClone.querySelectorAll('tbody tr');
            bodyRows.forEach(row => {
                const cells = row.querySelectorAll('td');
                if (cells.length > 0) {
                    // Check if it's the 'no rules' row by looking at colspan
                    const noRulesCell = cells[0];
                    if (noRulesCell && noRulesCell.hasAttribute('colspan')) {
                        // Decrease colspan if it's the 'no rules' row
                        const currentCs = parseInt(noRulesCell.getAttribute('colspan'), 10);
                        if (!isNaN(currentCs) && currentCs > 1) {
                            noRulesCell.setAttribute('colspan', (currentCs - 1).toString());
                        }
                    } else {
                        // Otherwise, remove the last cell (actions)
                        cells[cells.length - 1].remove();
                    }
                }
            });

            // Add specific classes to severity divs for print styling
            tableClone.querySelectorAll('[title]').forEach(el => {
                if (el.classList.contains('flex-shrink-0')) { // Target the severity divs
                    el.classList.add('severity-square');
                }
            });

            printWindow.document.write(tableClone.outerHTML);
        } else {
            printWindow.document.write(`<p>${t('securityFoundation.status.tableNotFound')}</p>`);
        }

        printWindow.document.write('</body></html>');
        printWindow.document.close();
        setTimeout(() => {
            printWindow.print();
            showSuccessToast(t('securityFoundation.status.printingInProgress'));
        }, 500);
    }, []);

    // PDF Export Handler
    const handleExportPDF = useCallback(async () => {
        try {
            showLoadingToast('Génération du rapport PDF en cours...');

            const generator = new SecurityFrameworkPdfGenerator(
                {
                    selectedRules: state.selectedRules,
                    frameworkNamesCache: state.frameworkNamesCache,
                    ruleDreadedEvents: state.ruleDreadedEvents,
                    ruleBusinessValues: state.ruleBusinessValues,
                    ruleGapComments: state.ruleGapComments
                },
                currentAnalysis,
                currentDreadedEvents || [],
                currentBusinessValues || []
            );

            const blob = await generator.generateSecurityFrameworkReport();

            // Download the PDF
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `socle-de-securite-${currentAnalysis?.name || 'rapport'}.pdf`;
            link.click();
            URL.revokeObjectURL(url);

            showSuccessToast('Rapport PDF généré avec succès');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showErrorToast('Erreur lors de la génération du PDF');
        }
    }, [state.selectedRules, state.frameworkNamesCache, currentAnalysis]);

    // PDF Export Handler (English)
    const handleExportPDFEn = useCallback(async () => {
        try {
            showLoadingToast('Generating PDF Report (EN)...');

            // Bilingual base generator in EN mode
            const generator = new SecurityFrameworkPdfGenerator(
                {
                    selectedRules: state.selectedRules,
                    frameworkNamesCache: state.frameworkNamesCache,
                    ruleDreadedEvents: state.ruleDreadedEvents,
                    ruleBusinessValues: state.ruleBusinessValues,
                    ruleGapComments: state.ruleGapComments
                },
                currentAnalysis,
                currentDreadedEvents || [],
                currentBusinessValues || [],
                'en'
            );

            const blob = await generator.generateSecurityFrameworkReport();

            // Download the PDF
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `security-baseline-${currentAnalysis?.name || 'report'}_EN.pdf`;
            link.click();
            URL.revokeObjectURL(url);

            showSuccessToast('PDF Report (EN) generated successfully');
        } catch (error) {
            console.error('Error generating PDF (EN):', error);
            showErrorToast('Error generating PDF (EN)');
        }
    }, [state.selectedRules, state.frameworkNamesCache, currentAnalysis]);

    // DOCX Export Handler
    const handleExportDocx = useCallback(async () => {
        try {
            showLoadingToast('Génération du rapport Word en cours...');

            const { generateSecurityFrameworkDocx } = await import('../../../utils/securityFrameworkDocxGenerator.js');

            await generateSecurityFrameworkDocx(
                {
                    selectedRules: state.selectedRules,
                    frameworks: currentSecurityFramework?.definitions || frameworkDefinitions,
                    customThemes: (currentSecurityFramework?.definitions || frameworkDefinitions).filter(f => f.is_new),
                    selectedFrameworks: frameworkDefinitions.filter(f => Object.keys(state.selectedRules).includes(f.stableId || f._id || f.id)),
                    ruleDreadedEvents: state.ruleDreadedEvents,
                    ruleBusinessValues: state.ruleBusinessValues,
                    ruleGapComments: state.ruleGapComments
                },
                currentAnalysis,
                currentDreadedEvents || [],
                currentBusinessValues || []
            );

            showSuccessToast('Rapport Word généré avec succès');
        } catch (error) {
            console.error('Error generating DOCX:', error);
            showErrorToast('Erreur lors de la génération du fichier Word');
        }
    }, [state.selectedRules, currentSecurityFramework, frameworkDefinitions, currentAnalysis]);


    // DOCX Export Handler (English)
    const handleExportDocxEn = useCallback(async () => {
        try {
            showLoadingToast('Generating Word report...');

            // Bilingual base generator in EN mode
            const { generateSecurityFrameworkDocx } = await import('../../../utils/securityFrameworkDocxGenerator.js');

            await generateSecurityFrameworkDocx(
                {
                    selectedRules: state.selectedRules,
                    frameworks: currentSecurityFramework?.definitions || frameworkDefinitions,
                    customThemes: (currentSecurityFramework?.definitions || frameworkDefinitions).filter(f => f.is_new),
                    selectedFrameworks: frameworkDefinitions.filter(f => Object.keys(state.selectedRules).includes(f.stableId || f._id || f.id)),
                    ruleDreadedEvents: state.ruleDreadedEvents,
                    ruleBusinessValues: state.ruleBusinessValues,
                    ruleGapComments: state.ruleGapComments
                },
                currentAnalysis,
                currentDreadedEvents || [],
                currentBusinessValues || [],
                'en'
            );

            showSuccessToast('Word Report (EN) generated successfully');
        } catch (error) {
            console.error('Error generating DOCX (EN):', error);
            showErrorToast('Error generating English Word file');
        }
    }, [state.selectedRules, currentSecurityFramework, frameworkDefinitions, currentAnalysis]);


    // FIXED: Calculate Select All checkbox state using the helper function
    const currentSelectedRuleIds = useMemo(() => {
        if (!selectedFramework) return new Set();

        const actualKey = getActualFrameworkKey(selectedFramework);
        const rules = state.selectedRules[actualKey] || [];

        return new Set(rules.map(r => r.id));
    }, [selectedFramework, state.selectedRules, getActualFrameworkKey]);

    const { isAllSelected, isIndeterminate } = useMemo(() => {
        if (!selectedFramework || filteredRulesForPanel.length === 0) {
            return { isAllSelected: false, isIndeterminate: false };
        }
        const visibleSelectedCount = filteredRulesForPanel.filter(rule =>
            currentSelectedRuleIds.has(rule.id)
        ).length;

        const allSelected = visibleSelectedCount === filteredRulesForPanel.length;
        const indeterminate = visibleSelectedCount > 0 && visibleSelectedCount < filteredRulesForPanel.length;

        return { isAllSelected: allSelected, isIndeterminate: indeterminate };

    }, [selectedFramework, filteredRulesForPanel, currentSelectedRuleIds]);

    // --- Render Logic (Use context loading/error states) ---

    // Combine loading states: Use context loading state directly
    const isLoading = analysisStore.isWorkshopDataLoading;
    // Combine error states (prioritize local SAVE error, then context WORKSHOP error)
    // Allow specific save error to override general workshop error for feedback
    const error = state.saveError || analysisStore.workshopDataError?.securityFramework || analysisStore.workshopDataError?.general;

    if (isLoading) {
        // Avoid rendering parts of the UI that depend on data not yet loaded
        return <div className="p-6 text-center"><p>{t('securityFoundation.status.loadingData')}</p></div>;
    }

    // Display workshop error if no specific save error exists
    if (error && !state.saveError) {
        return <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p className="font-semibold">{t('securityFoundation.status.loadingError')}</p>
            <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
        </div>;
    }

    // Display specific save error if it exists
    if (state.saveError) {
        // Maybe display this near the save button instead? Or use a toast?
        // For now, keep it simple:
        return <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p className="font-semibold">{t('securityFoundation.status.saveError')}</p>
            <p>{state.saveError}</p>
            {/* Add a button to clear the error? */}
        </div>;
    }

    // Check if definitions have loaded before rendering the main UI
    if (frameworkDefinitions.length === 0 && !isLoading) {
        // Handle case where definitions might be empty even if loading is finished
        return <div className="p-6 text-center text-gray-500">{t('securityFoundation.status.noFrameworkDefinitions')}</div>;
    }

    return (
        <div ref={formChangeRef} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            {/* Hidden File Input for CSV */}
            <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
                ref={fileInputRef} // Assign ref
            />

            {/* === MERGED HEADER === */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    {/* Left Side: Breadcrumb & Title */}
                    <div>
                        <div className="flex items-center text-sm text-slate-500 mb-2">
                            <span className="hover:text-blue-600 transition-colors">{t('securityFoundation.breadcrumb.workshop1')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-blue-600 font-medium">{t('securityFoundation.breadcrumb.securityFoundation')}</span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
                            <Shield size={28} className="mr-3 text-blue-600" />
                            {t('securityFoundation.title')}
                        </h1>
                    </div>

                    {/* Right Side: Action Buttons */}
                    <div className="flex items-center space-x-3">
                        {/* Import Button Group - Enhanced */}
                        <div className="inline-flex rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
                            <button
                                onClick={handleImportClick}
                                className="relative inline-flex items-center px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-b from-green-500 to-green-600 border-r border-green-700 rounded-l-lg hover:from-green-600 hover:to-green-700 focus:z-10 focus:ring-2 focus:ring-green-500 focus:outline-none transition-all duration-200"
                                title={t('securityFoundation.import.importTooltip')}
                            >
                                <Upload size={20} className="mr-2" />
                                {t('securityFoundation.buttons.import')}
                            </button>
                            <button
                                onClick={() => setShowImportInfo(true)}
                                className="relative inline-flex items-center px-3 py-2.5 text-sm font-medium text-white bg-gradient-to-b from-green-500 to-green-600 rounded-r-lg hover:from-green-600 hover:to-green-700 focus:z-10 focus:ring-2 focus:ring-green-500 focus:outline-none transition-all duration-200"
                                title={t('securityFoundation.import.infoTooltip', 'Info format CSV')}
                            >
                                <Info size={20} className="opacity-90 hover:opacity-100" />
                            </button>
                        </div>

                        {/* Save Button */}
                        <Button
                            variant="primary"
                            color="blue"
                            icon={<Save />}
                            onClick={handleSaveSecurityData}
                            disabled={state.isSaving || isLoading}
                            loading={state.isSaving}
                        >
                            {state.isSaving ? t('securityFoundation.buttons.saving') : t('securityFoundation.buttons.save')}
                        </Button>

                        {/* Unified Export Menu */}
                        <WorkshopExportMenu
                            onExportPdfFr={handleExportPDF}
                            onExportPdfEn={handleExportPDFEn}
                            onExportDocxFr={handleExportDocx}
                            onExportDocxEn={handleExportDocxEn}
                        />

                        {/* Help Button */}
                        <Button
                            variant="primary"
                            color="blue"
                            icon={<Info />}
                            onClick={() => setIsGuideOpen(true)}
                        >
                            {t('securityFoundation.buttons.help', 'Aide')}
                        </Button>
                    </div>
                </div>
            </div>
            {/* === END MERGED HEADER === */}

            {/* Guide Modal - positioned right under the header */}
            <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

            {/* Import Info Modal */}
            {showImportInfo && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Info size={20} className="text-blue-500 mr-2" />
                                {t('securityFoundation.import.infoTitle', 'Format CSV Attendu')}
                            </h3>
                            <button onClick={() => setShowImportInfo(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        <div className="text-sm text-gray-600 space-y-3">
                            <p>{t('securityFoundation.import.infoDescription', 'Le fichier CSV doit respecter la structure suivante (sans en-tête) :')}</p>
                            <div className="bg-gray-50 p-3 rounded border border-gray-200 font-mono text-xs">
                                Nom de la règle; Description de la règle
                            </div>
                            <p>{t('securityFoundation.import.example', 'Exemple :')}</p>
                            <div className="bg-gray-50 p-3 rounded border border-gray-200 font-mono text-xs">
                                Règle 1; Description de la règle 1<br />
                                Règle 2; Description de la règle 2
                            </div>
                            <p className="italic text-xs text-gray-500">
                                {t('securityFoundation.import.note', 'Note : Utilisez le point-virgule (;) comme séparateur.')}
                            </p>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowImportInfo(false)}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
                            >
                                {t('common.close', 'Fermer')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Import Modal */}
            {state.showImportModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('securityFoundation.import.title')}</h3>

                            {state.importError ? (
                                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                                    {state.importError}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Input for Framework Name */}
                                    <div>
                                        <label htmlFor="custom-framework-name" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('securityFoundation.import.frameworkName')}
                                        </label>
                                        <Input
                                            id="custom-framework-name"
                                            type="text"
                                            value={customFrameworkName}
                                            onChange={(e) => setCustomFrameworkName(e.target.value)}
                                            placeholder={t('securityFoundation.placeholders.frameworkName')}
                                            className="w-full"
                                        />
                                    </div>
                                    {/* Rules Preview */}
                                    <p className="text-sm text-gray-500">
                                        {state.csvImportData?.length || 0} {t('securityFoundation.status.rulesFound')}
                                    </p>
                                    <div className="max-h-60 overflow-y-auto border rounded p-4">
                                        {state.csvImportData?.map((rule, index) => (
                                            <div key={index} className="mb-2 p-2 bg-gray-50 rounded">
                                                <div className="font-medium">{rule.name}</div>
                                                <div className="text-sm text-gray-500">{rule.description}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                onClick={() => dispatch({ type: 'HIDE_IMPORT_MODAL' })}
                            >
                                {t('securityFoundation.buttons.cancel')}
                            </button>
                            <button
                                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                onClick={handleImportConfirm}
                                // Disable if no name, no data, or error
                                disabled={!customFrameworkName.trim() || !state.csvImportData || state.importError}
                            >
                                {t('securityFoundation.import.importButton')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}



            {/* Main Content Grid - Adjusted for wider cards */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
                {/* Left Column: Framework List */}
                <div className="lg:col-span-1">
                    {frameworkDefinitions.length > 0 ? (
                        <div className="sticky top-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">{t('securityFoundation.sections.frameworks')}</h3>
                                <span className="text-sm text-gray-500">{frameworkDefinitions.length} {t('securityFoundation.status.available')}</span>
                            </div>
                            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
                                {frameworkDefinitions.map(frameworkDef => {
                                    const frameworkKey = frameworkDef.stableId || frameworkDef._id || frameworkDef.id;
                                    const actualKey = getActualFrameworkKey(frameworkDef);
                                    const ruleCount = (state.selectedRules[actualKey] || []).length;
                                    return (
                                        <FrameworkListItem
                                            key={frameworkKey}
                                            framework={frameworkDef}
                                            isSelected={state.selectedFrameworkId === frameworkKey}
                                            onSelect={() => handleSelectFramework(frameworkKey)}
                                            selectedRuleCount={ruleCount}
                                            onDelete={handleDeleteFramework}
                                            onRename={handleRenameFramework}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center text-gray-500">
                            {t('securityFoundation.status.noFrameworkLoaded')}
                        </div>
                    )}
                </div>

                {/* Right Column: Details Panel (Pass context lists) */}
                <div className="lg:col-span-3">
                    {selectedFramework ? (
                        <Card className="p-6 shadow-md">
                            {/* Framework Header */}
                            <div className="mb-6 pb-4 border-b border-gray-200">
                                <h3 className="text-xl font-semibold text-gray-900 mb-1">{selectedFramework.name}</h3>
                                <p className="text-sm text-gray-500">{selectedFramework.description || t('securityFoundation.placeholders.configure')}</p>
                            </div>
                            {/* Rules Section */}
                            <div className="rules-selection-section space-y-6">
                                {/* Search and Available Rules */}
                                <div>
                                    <h4 className="text-sm uppercase tracking-wider text-gray-500 font-semibold mb-3">{t('securityFoundation.sections.availableRules')}</h4>
                                    <Input
                                        placeholder={t('securityFoundation.placeholders.searchRule')}
                                        value={state.searchTerm}
                                        onChange={handleSearchTermChange}
                                        className="mb-4 w-full"
                                    />
                                    {/* --- ADD Select All Checkbox --- */}
                                    <div className="flex items-center my-2 px-3 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                                        <input
                                            type="checkbox"
                                            id={`select-all-${selectedFramework._id || selectedFramework.id}`}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3 cursor-pointer"
                                            checked={isAllSelected}
                                            ref={el => el && (el.indeterminate = isIndeterminate)} // Set indeterminate state via ref
                                            onChange={handleSelectAllToggle} // Use the new handler
                                            disabled={filteredRulesForPanel.length === 0} // Disable if no rules visible
                                        />
                                        <label
                                            htmlFor={`select-all-${selectedFramework._id || selectedFramework.id}`}
                                            className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                                        >
                                            {isAllSelected ? t('securityFoundation.buttons.deselectAll') : t('securityFoundation.buttons.selectAll')} {t('securityFoundation.buttons.visible')}
                                        </label>
                                    </div>
                                    {/* Available Rules List */}
                                    <div className="border rounded-b-lg max-h-72 overflow-y-auto bg-white">
                                        {filteredRulesForPanel.length > 0 ? (
                                            <div className="divide-y divide-gray-100">
                                                {filteredRulesForPanel.map(ruleDef => {
                                                    // ... (existing RuleItem rendering logic) ...
                                                    const isSelected = currentSelectedRuleIds.has(ruleDef.id);
                                                    return (
                                                        <RuleItem
                                                            key={ruleDef.id}
                                                            rule={ruleDef}
                                                            isSelected={isSelected}
                                                            onToggle={() => handleToggleRule(getActualFrameworkKey(selectedFramework), ruleDef)}
                                                            disabled={false}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="p-6 text-center text-gray-500 text-sm">
                                                {state.searchTerm ? t('securityFoundation.status.noRuleFound') : t('securityFoundation.status.noRuleDefined')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Selected Rules Configuration (UPDATED with Pagination) - Shows ALL rules from ALL frameworks */}
                                <div>
                                    <div className="mb-3 flex justify-between items-center">
                                        <div className="flex items-center space-x-3">
                                            <h4 className="text-sm uppercase tracking-wider text-gray-500 font-semibold">{t('securityFoundation.sections.selectedRules')}</h4>
                                            <span className="text-sm text-gray-500">
                                            {allSelectedRulesDetails.length} {t('securityFoundation.status.selected')}
                                            </span>
                                        </div>
                                    </div>
                                    {allSelectedRulesDetails.length > 0 ? (
                                        <SelectedRulesWithPagination
                                            selectedRulesDetails={allSelectedRulesDetails}
                                            ruleCardRefs={ruleCardRefs}
                                            handleToggleRule={handleToggleRule}
                                            handleRuleChange={handleRuleChange}
                                            handleUpdateRuleBusinessValues={handleUpdateRuleBusinessValues}
                                            getActualFrameworkKey={getActualFrameworkKey}
                                            selectedFramework={selectedFramework}
                                            currentBusinessValues={currentBusinessValues}
                                            currentDreadedEvents={currentDreadedEvents}
                                            state={state}
                                            t={t}
                                            ruleIdToScrollTo={ruleIdToScrollTo}
                                            onScrollComplete={() => setRuleIdToScrollTo(null)}
                                        />
                                    ) : (
                                        <div className="p-8 text-center text-gray-400 border border-dashed rounded-lg">
                                            <p>{t('securityFoundation.status.noRuleSelected')}</p>
                                            <p className="text-sm mt-1">{t('securityFoundation.status.addRulesFromList')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ) : (
                        <div className="bg-white border border-dashed border-gray-300 rounded-lg flex items-center justify-center p-12 min-h-[400px]">
                            <p className="text-gray-500">
                                {frameworkDefinitions.length > 0 ? t('securityFoundation.status.selectFramework') : t('securityFoundation.status.loadingFrameworks')}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* ponytail: radar chart, replace with donut if proportions matter */}
            {summaryDataForTable.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                    <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">
                        {t('securityFoundation.sections.statusDistribution', 'Répartition des statuts')}
                    </h4>
                    <div className="flex justify-center">
                        <div className="w-full max-w-md">
                            <ResponsiveContainer width="100%" height={220}>
                                <RadarChart data={statusDistribution} cx="50%" cy="50%" outerRadius="70%">
                                    <PolarGrid stroke="#E5E7EB" />
                                    <PolarAngleAxis dataKey="status" tick={{ fill: '#6B7280', fontSize: 12 }} />
                                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                                    <Radar
                                        name="Règles"
                                        dataKey="count"
                                        stroke="#6366F1"
                                        fill="#6366F1"
                                        fillOpacity={0.3}
                                        strokeWidth={2}
                                    />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0].payload;
                                            return (
                                                <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-sm border border-gray-700 pointer-events-none">
                                                    <p className="font-semibold">{d.status}</p>
                                                    <p className="text-gray-300 mt-0.5">{d.count} règle{d.count > 1 ? 's' : ''}</p>
                                                </div>
                                            );
                                        }}
                                    />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Table - Removed button props */}
            {summaryDataForTable.length > 0 ? (
                <div id="summary-table-section">
                    <SecurityFrameworkSummaryTable
                        rulesToDisplay={summaryDataForTable}
                        onModifyRule={handleModifyRule}
                        onDeleteRule={handleDeleteRule}
                        ruleGapComments={state.ruleGapComments}
                        onUpdateRuleGapComment={handleUpdateRuleGapComment}
                        allDreadedEvents={currentDreadedEvents || []}
                        ruleDreadedEvents={state.ruleDreadedEvents}
                        onUpdateRuleDreadedEvents={handleUpdateRuleDreadedEvents}
                        onCreateDreadedEvent={handleCreateDreadedEvent}
                        allBusinessValues={currentBusinessValues || []}
                    />
                </div>
            ) : (
                <div className="mt-8 p-6 bg-white rounded-lg border border-gray-200 text-center text-gray-500">
                    {t('securityFoundation.status.noRuleSelectedSummary')}
                </div>
            )}

            {/* Deletion Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setRuleToDeleteData(null);
                }}
                onConfirm={confirmDelete}
                itemName={ruleToDeleteData?.ruleToDelete?.name}
                itemType="securityRule"
            />

            {isFrameworkDeleteModalOpen && frameworkToDelete && (
                <DeleteConfirmationModal
                    isOpen={isFrameworkDeleteModalOpen}
                    onClose={() => { setIsFrameworkDeleteModalOpen(false); setFrameworkToDelete(null); }}
                    onConfirm={confirmDeleteFramework}
                    title={t('securityFoundation.deleteFramework.title', 'Supprimer le référentiel')}
                    message={t('securityFoundation.deleteFramework.message', { name: frameworkToDelete.name, count: frameworkToDelete.count }) || `Supprimer "${frameworkToDelete.name}" ? ${frameworkToDelete.count ? `(${frameworkToDelete.count} règles associées seront retirées)` : ''}`}
                />
            )}

            {/* Deselection Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={!!deselectConfirmData}
                onClose={() => setDeselectConfirmData(null)}
                onConfirm={confirmDeselect}
                itemName={deselectConfirmData?.type === 'single' ? deselectConfirmData.rules[0]?.name : (deselectConfirmData?.type === 'multiple' ? deselectConfirmData.rules.length : undefined)}
                itemType={deselectConfirmData?.type === 'single' ? "securityRule" : "bulkMeasures"}
                title={deselectConfirmData?.type === 'single' ? t('securityFoundation.confirmations.deselectRuleTitle') : t('securityFoundation.confirmations.deselectMultipleRulesTitle')}
                message={deselectConfirmData?.type === 'single' ? t('securityFoundation.confirmations.deselectRuleMessage') : t('securityFoundation.confirmations.deselectMultipleRulesMessage')}
                confirmText={t('securityFoundation.buttons.confirm')}
            />

            {/* AI Rule Selection Modal */}
            {showAiRuleSelectionModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 border-b flex-shrink-0">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-white flex items-center">
                                        <BrainCircuit className="w-6 h-6 mr-2" />
                                        {t('securityFoundation.ai.selectRulesTitle', 'Sélection des règles pour l\'IA')}
                                    </h3>
                                    <p className="text-blue-100 text-sm mt-1">
                                        {t('securityFoundation.ai.selectRulesSubtitle', `Sélectionnez les règles à associer automatiquement aux événements redoutés.`)}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowAiRuleSelectionModal(false)}
                                    className="text-white hover:text-gray-200 p-2 rounded-lg hover:bg-white/10 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto flex-grow">
                            <div className={`mb-4 flex items-center justify-between p-3 rounded-lg border ${
                                selectedRulesForAi.length >= 10
                                ? 'bg-red-50 border-red-200'
                                : 'bg-blue-50 border-blue-100'
                            }`}>
                                <div className="flex items-center">
                                    <Info className={`w-5 h-5 mr-2 ${selectedRulesForAi.length >= 10 ? 'text-red-500' : 'text-blue-500'}`} />
                                    <span className={`text-sm ${selectedRulesForAi.length >= 10 ? 'text-red-800' : 'text-blue-800'}`}>
                                        {t('securityFoundation.ai.batchProcessingInfo', 'Vous pouvez sélectionner jusqu\'à 10 règles maximum par analyse.')}
                                    </span>
                                </div>
                                <span className={`font-semibold whitespace-nowrap ml-4 ${selectedRulesForAi.length >= 10 ? 'text-red-700' : 'text-blue-700'}`}>
                                    {selectedRulesForAi.length} / 10 max
                                </span>
                            </div>

                            <div className="flex justify-between items-center mb-3 px-2">
                                <button
                                    onClick={() => {
                                        const unmatched = allSelectedRulesDetails.filter(r => (state.ruleDreadedEvents[r.id] || []).length === 0);
                                        if (selectedRulesForAi.length === Math.min(unmatched.length, 10)) {
                                            setSelectedRulesForAi([]); // Deselect all
                                        } else {
                                            setSelectedRulesForAi(unmatched.map(r => r.id).slice(0, 10)); // Select up to 10
                                        }
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    {selectedRulesForAi.length === Math.min(allSelectedRulesDetails.filter(r => (state.ruleDreadedEvents[r.id] || []).length === 0).length, 10)
                                        ? t('securityFoundation.ai.deselectAllBtn', 'Tout désélectionner')
                                        : t('securityFoundation.ai.selectAllBtn', 'Tout sélectionner')}
                                </button>
                            </div>

                            <div className="space-y-2 border rounded-lg p-2 max-h-[40vh] overflow-y-auto bg-gray-50">
                                {allSelectedRulesDetails
                                    .filter(rule => (state.ruleDreadedEvents[rule.id] || []).length === 0)
                                    .map(rule => (
                                        <label key={rule.id} className={`flex items-start p-3 bg-white border border-gray-200 rounded-md hover:bg-gray-50 ${!selectedRulesForAi.includes(rule.id) && selectedRulesForAi.length >= 10 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                            <div className="flex items-center h-5 mt-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRulesForAi.includes(rule.id)}
                                                    disabled={!selectedRulesForAi.includes(rule.id) && selectedRulesForAi.length >= 10}
                                                    onChange={(e) => {
                                                        if (!e.target.checked) {
                                                            setSelectedRulesForAi(prev => prev.filter(id => id !== rule.id));
                                                        } else if (selectedRulesForAi.length < 10) {
                                                            setSelectedRulesForAi(prev => [...prev, rule.id]);
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                            </div>
                                            <div className="ml-3 text-sm flex-1">
                                                <span className="font-medium text-gray-900 block">{rule.name}</span>
                                                {rule.description && (
                                                    <span className="text-gray-500 line-clamp-2 mt-1">{rule.description}</span>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 rounded-b-lg flex-shrink-0">
                            <button
                                onClick={() => setShowAiRuleSelectionModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button
                                onClick={handleStartBatchAIMatching}
                                disabled={selectedRulesForAi.length === 0 || isGlobalAIMatching}
                                className={`px-4 py-2 rounded-md text-sm font-medium flex items-center ${
                                    selectedRulesForAi.length === 0 || isGlobalAIMatching
                                    ? 'bg-blue-300 text-white cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isGlobalAIMatching ? (
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <BrainCircuit className="w-4 h-4 mr-2" />
                                )}
                                {t('securityFoundation.ai.startMatchingBtn', 'Démarrer l\'analyse IA')} ({selectedRulesForAi.length})
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* AI Progress Modal */}
            {showAiProgressModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                        <div className="text-center">
                            <BrainCircuit className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-pulse" />
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {t('securityFoundation.ai.analyzingTitle', 'Analyse IA en cours...')}
                            </h3>
                            <p className="text-sm text-gray-500 mb-6">
                                {t('securityFoundation.ai.analyzingSubtitle', 'Veuillez patienter pendant que l\'IA analyse et associe vos règles.')}
                            </p>
                            
                            {/* Simple Spinner */}
                            <div className="flex justify-center mb-6">
                                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                            </div>
                            
                            {/* Cancel Button */}
                            <button
                                onClick={() => {
                                    if (abortControllerRef.current) {
                                        abortControllerRef.current.abort();
                                    }
                                }}
                                className="px-5 py-2.5 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:border-red-300 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm"
                            >
                                {t('securityFoundation.buttons.cancelOperation', 'Annuler l\'opération')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* AI Matching Suggestions Modal */}
            {showAiMatchingModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        {/* Modal Header */}
                        <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 border-b">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold text-white flex items-center">
                                        <BrainCircuit className="w-6 h-6 mr-2" />
                                        {t('securityFoundation.ai.suggestionsTitle', 'Suggestions d\'associations IA')}
                                    </h3>
                                    <p className="text-purple-100 text-sm mt-1">
                                        {t('securityFoundation.ai.suggestionsSubtitle', `${aiMatchingSuggestions.length} règle(s) avec des suggestions`)}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCloseAiMatchingModal}
                                    className="text-white hover:text-gray-200 p-2 rounded-lg hover:bg-white/10 transition-colors"
                                    title={t('securityFoundation.buttons.close')}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            <div className="space-y-6">
                                {aiMatchingSuggestions.map((suggestion, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                        {/* Rule Header */}
                                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                            <h4 className="font-semibold text-gray-900">{suggestion.rule.name}</h4>
                                            {suggestion.rule.description && (
                                                <p className="text-sm text-gray-600 mt-1">{suggestion.rule.description}</p>
                                            )}
                                        </div>

                                        {/* Matched Events */}
                                        <div className="p-4 bg-white">
                                            <div className="mb-3">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {t('securityFoundation.ai.suggestedEvents', 'Événements redoutés suggérés')} ({suggestion.matchedEvents.length})
                                                </span>
                                            </div>
                                            <div className="space-y-3">
                                                {suggestion.matchedEvents.map((event, eventIndex) => (
                                                    <div key={eventIndex} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                        <div className="flex items-start">
                                                            <div className="flex-shrink-0 mt-0.5">
                                                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                            </div>
                                                            <div className="ml-3 flex-1">
                                                                <p className="font-medium text-gray-900">{event.name}</p>
                                                                {event.description && (
                                                                    <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Action Buttons for this suggestion */}
                                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                                            <button
                                                onClick={() => handleRejectSuggestion(index)}
                                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                            >
                                                {t('securityFoundation.buttons.reject', 'Refuser')}
                                            </button>
                                            <button
                                                onClick={() => handleAcceptSuggestion(index)}
                                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                                            >
                                                {t('securityFoundation.buttons.accept', 'Accepter')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-sm text-gray-600">
                                {t('securityFoundation.ai.reviewInstructions', 'Examinez chaque suggestion et acceptez ou refusez individuellement')}
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    onClick={handleCloseAiMatchingModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                >
                                    {t('securityFoundation.buttons.cancel', 'Annuler')}
                                </button>
                                <button
                                    onClick={handleAcceptAllSuggestions}
                                    className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-md hover:from-purple-700 hover:to-blue-700 transition-colors shadow-lg"
                                >
                                    {t('securityFoundation.buttons.acceptAll', 'Accepter tout')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default SecurityFramework;

// --- END OF FILE SecurityFramework.js ---
